import {
  APIComponentAttributes,
  APIDateOnlyFieldAttributes,
  APIDateTimeFieldAttributes,
  APIReferenceAttributes,
  ArdoqId,
  ColorRangeFormatting,
  ComparatorMatcher,
  FilterTypes,
  FormattingRuleOperator,
  isColorRangeFormatting,
  isComponent,
  isConditionalFormatting,
  isFormatting,
  isListFieldFormatting,
  isReference,
} from '@ardoq/api-types';
import { isNil } from 'lodash';
import { resolveColorRangeColor, toNumericFieldValue } from './colorRangeUtils';
import { getFieldSortedValuesFromScopeData } from './getFieldSortedValuesFromScopeData';
import { componentOperations, fieldOperations } from '@ardoq/core';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { dateRangeOperations } from '@ardoq/date-range';
import { DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';
import { referenceOperations } from '@ardoq/reference-operations';
import {
  enhancedScopeDataOperations,
  fieldUtils,
  referenceUtils,
} from '@ardoq/scope-data';

import { AttributeComparator } from './AttributeComparator';
import {
  comparators as commonComparators,
  getIdDoesNotMatchComparator,
  getIdMatchComparator,
} from './attributeComparators';
import {
  filterKeyOperations,
  isDefaultComponentAttribute,
  isDefaultReferenceAttribute,
} from './filterEnums';
import {
  isResult,
  isSpecialAttributeType,
  parseFilterValue,
} from './filterUtils';
import { isHasReferenceFromFilter, isHasReferenceToFilter } from './predicates';
import {
  isEntityTypeIncluded,
  legacyAffectsTypeNames,
} from './isEntityTypeIncluded';
import {
  isListFieldIncluded,
  resolveListFieldColor,
} from './listFieldFormattingUtils';
import {
  getRelativeDateValue,
  isDateInRelativeDateRange,
  isDateRangeInRelativeDateRange,
} from './relativeDateFormattingUtils';

const comparators: Record<string, AttributeComparator> = {
  ...commonComparators,
  idMatch: getIdMatchComparator(() => undefined),
  idDoesNotMatch: getIdDoesNotMatchComparator(() => undefined),
};

const AttributeComparators = {
  getById: (id: string): AttributeComparator | undefined => comparators[id],
};

const appliesToEntity = (filter: ComparatorMatcher, entity: unknown) => {
  return (
    (filter.affectComponent && isComponent(entity)) ||
    (filter.affectReference && isReference(entity))
  );
};

const modelHasAttribute = (
  scopeData: EnhancedScopeData,
  obj: APIComponentAttributes | APIReferenceAttributes,
  filteredAttributeName: string
) =>
  modelHasAttributeWithField(scopeData, obj, filteredAttributeName)
    .hasAttribute;

const resolveBoundedDateRangeFieldValue = (
  obj: APIComponentAttributes | APIReferenceAttributes,
  fields: {
    field: APIDateOnlyFieldAttributes | APIDateTimeFieldAttributes;
    counterpart: APIDateOnlyFieldAttributes | APIDateTimeFieldAttributes;
  }
) => {
  const { field, counterpart } = fields;
  const currentFieldValue = fieldOperations.getFieldValue(field, obj);
  const counterpartValue = fieldOperations.getFieldValue(counterpart, obj);

  return dateRangeOperations.replaceEmptyDateRangePartFieldValue({
    fieldName: field.name,
    fieldType: field.type,
    currentFieldValue,
    dateRangeFieldCounterpartValue: counterpartValue,
  });
};

const getFieldForFilterAttribute = (
  scopeData: EnhancedScopeData,
  obj: APIComponentAttributes | APIReferenceAttributes,
  filteredAttributeName: string
) => {
  const modelTypePair = isReference(obj)
    ? referenceOperations.getEntityTypePair(obj)
    : componentOperations.getEntityTypePair(obj);

  const fields = fieldUtils.getFieldsByType(scopeData, modelTypePair);

  return Object.values(fields).find(
    field => field.name === filteredAttributeName
  );
};

const isDefaultAttribute = (
  obj: APIComponentAttributes | APIReferenceAttributes,
  attributeName: string
) => {
  if (isComponent(obj)) {
    return isDefaultComponentAttribute(attributeName);
  }
  if (isReference(obj)) {
    return isDefaultReferenceAttribute(attributeName);
  }
  return false;
};

const modelHasAttributeWithField = (
  scopeData: EnhancedScopeData,
  obj: APIComponentAttributes | APIReferenceAttributes,
  filteredAttributeName: string
) => {
  if (isDefaultAttribute(obj, filteredAttributeName)) {
    return { hasAttribute: true };
  }
  const field = getFieldForFilterAttribute(
    scopeData,
    obj,
    filteredAttributeName
  );
  return field ? { hasAttribute: true, field } : { hasAttribute: false };
};

/**
 * Check if models which are missing the test field should be included.
 *
 * Models missing the test field should be included if this filter is an
 * excluding filter. A filter is an excluding filter if the main logical goal
 * is to exclude elements that matches a criteria. In many cases this is
 * equivalent to being an inverted filter, because our filters are mainly
 * defined as including filters.
 *
 * The reason we need a function for this, and cannot rely on simply using
 * `filter.isNegative`, is edge cases. These are:
 *  * `"is empty"` is implemented as `"equals undefined/null/''"`, which is an
 *      inclusive filter (include where value is nothing), even though the
 *      logical meaning is negative: Exclude everything with a value.
 *  * `"is not empty"` is implemented as `"inverted equals undefined/null/''"`,
 *      which is a excluding filter (exclude where value is nothing), even
 *      though the logical meaning is positive: Include everything with a
 *      value
 */
export const shouldModelWithoutFieldBeIncluded = (
  filter: Pick<ComparatorMatcher, 'isNegative' | 'value' | 'comparator'>
) => {
  // IS_EMPTY / IS_NOT_EMPTY should only match components that HAVE the field.
  // A missing field is neither "empty" nor "not empty".
  if (
    filter.comparator === FormattingRuleOperator.IS_EMPTY ||
    filter.comparator === FormattingRuleOperator.IS_NOT_EMPTY
  ) {
    return false;
  }
  const isNegative = filter.isNegative;
  const isEmptyCheckFilter = filter.value === null;
  return isNegative ? !isEmptyCheckFilter : isEmptyCheckFilter;
};

const areAttributesIncluded = (
  scopeData: EnhancedScopeData,
  obj: APIComponentAttributes | APIReferenceAttributes,
  filter: ComparatorMatcher,
  filterAttributeNames: string[],
  isNegative: boolean
) => {
  const hasAttributes = filterAttributeNames.every(filterAttributeName =>
    modelHasAttribute(scopeData, obj, filterAttributeName)
  );
  if (hasAttributes) {
    if (filter.comparator) {
      const relativeDateValue = getRelativeDateValue(
        isConditionalFormatting(filter),
        filter.value,
        filter.type,
        filter.comparator
      );
      if (relativeDateValue) {
        const [startVal, endVal] = filterAttributeNames.map(name => obj[name]);
        const isInRange = isDateRangeInRelativeDateRange(
          startVal,
          endVal,
          relativeDateValue
        );
        return isNegative ? !isInRange : isInRange;
      }
    }

    const values = filterAttributeNames.map(
      filterAttributeName => obj[filterAttributeName]
    );
    const comparator = AttributeComparators.getById(filter.comparator ?? '');
    if (!comparator) return shouldModelWithoutFieldBeIncluded(filter);
    const isIncluded = comparator.isIncluded(values, filter.value);
    return isNegative ? !isIncluded : isIncluded;
  }
  return shouldModelWithoutFieldBeIncluded(filter);
};

const handleSpecialAttributeType = (
  filterAttributeName: string,
  filterValue: string,
  _componentId: ArdoqId,
  contextComponent?: APIComponentAttributes
) => {
  const filterValueOrResult = parseFilterValue({
    filterValue,
    currentComponentId: contextComponent?._id,
  });
  if (isResult(filterValueOrResult)) {
    // Will always be true, as no code currently short-circuits to never
    // match anything
    return filterValueOrResult;
  }

  if (isHasReferenceToFilter(filterAttributeName)) {
    return false; // TODO: Implement for ardoq-front
  }
  if (isHasReferenceFromFilter(filterAttributeName)) {
    return false; // TODO: Implement for ardoq-front
  }
  return false;
};

const isAttributeIncluded = (
  obj: APIComponentAttributes | APIReferenceAttributes,
  filter: ComparatorMatcher,
  scopeData: EnhancedScopeData,
  contextComponent?: APIComponentAttributes
) => {
  if (!appliesToEntity(filter, obj)) {
    return true;
  }

  // TODO(typeName-migration): Remove affectsTypeName and legacyAffectsTypeNames; use affectsTypeNames directly.
  const typeNameFilter = legacyAffectsTypeNames(
    filter.affectsTypeName,
    filter.affectsTypeNames
  );
  const isFormattingFilter = isConditionalFormatting(filter);
  if (
    isFormattingFilter &&
    typeNameFilter &&
    !isEntityTypeIncluded({
      filterAttributeName: filter.name,
      typeNameFilter,
      entityTypeName: isComponent(obj)
        ? obj.type
        : scopeData.typesByModelId[obj.model]?.referenceTypesById[obj.type]
            ?.name,
    })
  ) {
    return false;
  }

  const isNegative = filter.isNegative;
  const filterAttributeName = filter.name;
  const filterValue = filter.value;

  // TODO: Old format? I think these are separate filters now
  if (filterAttributeName.includes(',')) {
    return areAttributesIncluded(
      scopeData,
      obj,
      filter,
      filterAttributeName.split(','),
      isNegative
    );
  }

  if (isSpecialAttributeType(filterAttributeName)) {
    const isIncluded = handleSpecialAttributeType(
      filterAttributeName,
      filterValue as string,
      obj._id,
      contextComponent
    );

    return isNegative ? !isIncluded : isIncluded;
  }
  const hasAttributeAndField = modelHasAttributeWithField(
    scopeData,
    obj,
    filterAttributeName
  );
  const { field } = hasAttributeAndField;

  let { hasAttribute } = hasAttributeAndField;
  let val = field
    ? fieldOperations.getFieldValue(field, obj)
    : obj[filterAttributeName];

  if (!val && filterKeyOperations.isIncomingRefCount(filterAttributeName)) {
    hasAttribute = true;
    val = enhancedScopeDataOperations.countIncomingReferences(
      scopeData,
      obj._id
    );
  } else if (
    !val &&
    filterKeyOperations.isOutgoingRefCount(filterAttributeName)
  ) {
    hasAttribute = true;
    val = enhancedScopeDataOperations.countOutgoingReferences(
      scopeData,
      obj._id
    );
  } else if (
    filterKeyOperations.isTypeKey(filterAttributeName) &&
    filter.affectReference
  ) {
    const modelType = referenceUtils.getTypeOfReference(scopeData, obj._id);
    val = modelType?.name;
  }

  if (isNil(val)) {
    if (field) {
      // TODO: ardoq-front needs:
      // - excludeTime option
      // - APIFieldType.USER (NEED ORG USERS) handling
      // - APIFieldType.DATE_ONLY_RANGE handling
      // - APIFieldType.DATE_TIME_RANGE handling
      // DEFAULT_ARDOQ_I18NLOCALE OK here: filter comparators match against
      // the canonical English placeholder values stored in saved filters.
      val = fieldUtils.format({
        scopeData,
        fieldId: field._id,
        value: val,
        i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
      });
      hasAttribute = true;
    }
  }

  if (field && dateRangeOperations.fieldIsPartOfDateRangeField(field)) {
    const counterpart = fieldUtils.getCounterpartField(scopeData, field);
    if (counterpart) {
      val = resolveBoundedDateRangeFieldValue(obj, { field, counterpart });
    }
  }

  if (isFormattingFilter && isColorRangeFormatting(filterValue)) {
    if (filterValue.min.value === null || filterValue.max.value === null) {
      return true;
    }
    return toNumericFieldValue(val) !== undefined;
  }

  if (hasAttribute) {
    if (isFormattingFilter && isListFieldFormatting(filterValue)) {
      return isListFieldIncluded(val, filterValue);
    }

    const relativeDateValue = getRelativeDateValue(
      isFormattingFilter,
      filterValue,
      filter.type,
      filter.comparator ?? ''
    );
    if (relativeDateValue) {
      const isInRange = isDateInRelativeDateRange(val, relativeDateValue);
      return isNegative ? !isInRange : isInRange;
    }

    const comparator = AttributeComparators.getById(filter.comparator ?? '');
    if (!comparator) return shouldModelWithoutFieldBeIncluded(filter);
    const isIncluded = comparator.isIncluded(val, filterValue);
    if (isNegative) return !isIncluded;
    return isIncluded;
  }
  return shouldModelWithoutFieldBeIncluded(filter);
};

const getFilterColor = (
  filter: ComparatorMatcher,
  obj: APIComponentAttributes | APIReferenceAttributes,
  scopeData: EnhancedScopeData
): string | undefined => {
  if (
    !isListFieldFormatting(filter.value) &&
    !isColorRangeFormatting(filter.value)
  ) {
    return filter.color;
  }

  if (isListFieldFormatting(filter.value)) {
    const fieldValue = obj[filter.name];
    return resolveListFieldColor(fieldValue, filter.value);
  }

  const value = getColorRangeFieldValue(filter.name, obj, scopeData);
  if (value === undefined) {
    return undefined;
  }

  return createColorRangeColorResolver(
    filter,
    filter.value,
    scopeData,
    isComponent(obj)
  )(value);
};

/**
 * The value a gradient rule reads off an entity: the named field, or the entity's incoming or
 * outgoing reference count.
 */
export const getColorRangeFieldValue = (
  attributeName: string,
  obj: APIComponentAttributes | APIReferenceAttributes,
  scopeData: EnhancedScopeData
): number | undefined =>
  toNumericFieldValue(
    filterKeyOperations.isIncomingRefCount(attributeName)
      ? enhancedScopeDataOperations.countIncomingReferences(scopeData, obj._id)
      : filterKeyOperations.isOutgoingRefCount(attributeName)
        ? enhancedScopeDataOperations.countOutgoingReferences(
            scopeData,
            obj._id
          )
        : obj[attributeName]
  );

/**
 * The color a gradient rule gives a value, ranked for percent and percentile modes against the
 * entities the rule affects.
 */
export const createColorRangeColorResolver = (
  filter: Pick<
    ComparatorMatcher,
    'name' | 'affectsTypeName' | 'affectsTypeNames'
  >,
  colorRangeFormatting: ColorRangeFormatting,
  scopeData: EnhancedScopeData,
  isComponentType: boolean
): ((value: number) => string | undefined) => {
  const sortedValues =
    colorRangeFormatting.mode === 'percent' ||
    colorRangeFormatting.mode === 'percentile'
      ? getFieldSortedValuesFromScopeData(
          scopeData,
          filter.name,
          isComponentType,
          legacyAffectsTypeNames(
            filter.affectsTypeName,
            filter.affectsTypeNames
          )
        )
      : null;

  return value =>
    resolveColorRangeColor(colorRangeFormatting, value, sortedValues);
};

const isTagIncluded = (
  obj: APIComponentAttributes | APIReferenceAttributes,
  filter: ComparatorMatcher,
  scopeData: EnhancedScopeData
) => {
  if (!appliesToEntity(filter, obj)) {
    return true;
  }

  const tagsWithName = scopeData.tags.filter(tag => tag.name === filter.name);

  if (!tagsWithName.length) {
    return Boolean(filter.isNegative);
  }

  const isTagged = tagsWithName.some(tag =>
    isComponent(obj)
      ? tag.components.includes(obj._id)
      : tag.references.includes(obj._id)
  );

  return filter.isNegative ? !isTagged : isTagged;
};

const isIncluded = (
  obj: APIComponentAttributes | APIReferenceAttributes,
  filter: ComparatorMatcher,
  scopeData: EnhancedScopeData,
  contextComponent?: APIComponentAttributes
) => {
  const { type } = filter;

  switch (type) {
    case FilterTypes.ATTRIBUTE:
      return isAttributeIncluded(obj, filter, scopeData, contextComponent);
    case FilterTypes.DATE_RANGE:
      return isAttributeIncluded(obj, filter, scopeData, contextComponent);
    case FilterTypes.TAG:
      return isTagIncluded(obj, filter, scopeData);
    case FilterTypes.SYSTEM_FILTER:
      return true;
    default:
      return false;
  }
};

export const isReferenceIncludedInContextByFilter = (
  reference: APIReferenceAttributes | undefined,
  filters: ComparatorMatcher[],
  scopeData: EnhancedScopeData
): { included: boolean; color?: string } => {
  if (!reference) {
    return { included: false, color: '' };
  }

  const isReferenceObj = isReference(reference);

  const included = filters
    .filter(filter => !isFormatting(filter))
    .every(filter => isIncluded(reference, filter, scopeData));

  const firstColorFilter = filters
    .filter(isConditionalFormatting)
    .filter(filter => isIncluded(reference, filter, scopeData))
    .find(filter => filter.affectReference && isReferenceObj);

  const colorMatch = firstColorFilter
    ? getFilterColor(firstColorFilter, reference, scopeData)
    : undefined;
  return { included, color: colorMatch };
};

export type FilterData = {
  included: boolean;
  color?: string;
};
export const isComponentIncludedInContextByFilter = (
  component: APIComponentAttributes | undefined,
  filters: ComparatorMatcher[],
  contextComponent: APIComponentAttributes | undefined,
  scopeData: EnhancedScopeData
): FilterData => {
  if (!component) {
    return { included: false, color: '' };
  }

  const isComponentObj = isComponent(component);

  const included = filters
    .filter(filter => !isFormatting(filter))
    .every(filter =>
      isIncluded(component, filter, scopeData, contextComponent)
    );

  const firstColorFilter = filters
    .filter(isConditionalFormatting)
    .filter(filter =>
      isIncluded(component, filter, scopeData, contextComponent)
    )
    .find(filter => filter.affectComponent && isComponentObj);

  const colorMatch = firstColorFilter
    ? getFilterColor(firstColorFilter, component, scopeData)
    : undefined;

  return { included, color: colorMatch };
};
